import { DiscoveryFeature } from '@utils/shouldShowDiscoveryCard';

/**
 * A ordem de prioridade dos convites, e **um por tela de cada vez**.
 *
 * Por que um cap existe: os convites não caem em comunidades diferentes, caem todos na mesma. Medido
 * na base ativa (2.409 comunidades), contando oito predicados de descoberta por comunidade:
 *
 * | predicados falhando | comunidades |
 * |---|---|
 * | 8 | 1.641 |
 * | 7 | 547 |
 * | 6 | 158 |
 * | 5 ou menos | 63 |
 *
 * 68% falham **todos os oito**, e 91% falham sete ou oito. Ou seja: quem recebe um convite recebe
 * quase todos, e cada card novo não amplia cobertura — empilha em cima de quem já estava recebendo.
 * Sem cap, o criador típico abriria a home com uma parede de avisos, e a resposta previsível a uma
 * parede de avisos é aprender a ignorar o X.
 *
 * A ordem abaixo vai do dinheiro já em movimento para o dinheiro que ainda não existe:
 *
 * 1. `remarketing`, `downsell` — venda que começou e não terminou. É a recuperação de dinheiro que já
 *    estava na mesa.
 * 2. `coupon` — recompra, que é o fator de maior separação de receita medido na base (R$ 2,28 por
 *    usuário novo acima de 1,15 fatura por comprador, contra R$ 0,77 abaixo de 1,05).
 * 3. `upsell`, `bump` — aumentar o valor de uma venda que está acontecendo.
 * 4. `integrations` — não vende nada por si, mas é o que faz o criador saber onde investir.
 * 5. `scheduleMessages` — cadência, que depende de audiência já formada.
 * 6. `affiliation`, `referral` — trazer gente nova. O retorno é o mais lento dos sete, e é por isso
 *    que fica no fim: quem tem venda abandonada não resolvida não deveria ser convidado a recrutar
 *    vendedor antes de recuperar o que já perdeu.
 *
 * O cap é por TELA, não global: a home e o relatório de Vendas mostram um cada, porque quem está no
 * relatório de Vendas foi ali por um motivo e o convite dessa tela fala do que está na tela.
 */
export const DISCOVERY_PRIORITY: DiscoveryFeature[] = [
  'remarketing',
  'downsell',
  'coupon',
  'upsell',
  'bump',
  'integrations',
  'scheduleMessages',
  'affiliation',
  'referral',
];

/**
 * Qual dos candidatos aparece. Recebe o que cada `useDiscoveryCard` já decidiu, e devolve o de maior
 * prioridade — ou `null` quando nenhum quer aparecer.
 *
 * Recebe o mapa pronto, e não os argumentos dos hooks, de propósito: cada hook continua sendo a
 * autoridade sobre se ELE pode aparecer (permissão, toggle, evidência, dispensa), e esta função só
 * desempata. Assim o cap não pode ressuscitar um card que o hook vetou.
 *
 * Feature fora de {@link DISCOVERY_PRIORITY} não aparece nunca, em vez de aparecer por último: card
 * novo que alguém esqueça de priorizar fica invisível e o teste pega, o que é melhor do que ele
 * furar a fila em silêncio.
 */
export type DiscoveryCandidate = boolean | 'pending';

export const pickDiscoveryCard = (
  candidates: Partial<Record<DiscoveryFeature, DiscoveryCandidate>>,
): DiscoveryFeature | null => {
  for (const feature of DISCOVERY_PRIORITY) {
    const candidate = candidates[feature];

    /*
      Candidato de maior prioridade ainda carregando segura a fila: nada aparece até ele decidir.

      Cada `useDiscoveryCard` busca o próprio toggle, e eles resolvem em tempos diferentes. Sem esta
      espera, "ainda não sei" e "não quero" ficam indistinguíveis, e o convite de baixo aparece só
      porque o de cima demorou — para ser trocado um instante depois. Entre os dois, o criador pode ler
      o convite errado, ou dispensá-lo: a dispensa é gravada por 30 dias e cala um convite que ele nem
      chegou a escolher.
    */
    if (candidate === 'pending') return null;
    if (candidate === true) return feature;
  }

  return null;
};
